import { client } from '../sanity/sanityClient';

// ---------------------------------------------------------------------
// Feed service
//
// This module owns every query/mutation that touches the ranking
// algorithm so components never construct GROQ by hand. It replaces
// the old "fetch every post, shuffle client-side" approach with:
//
//   1. Cursor-based pagination (only fetch the next page, not everything)
//   2. A decayed-engagement score used for ordering
//   3. An optional personalized blend (followed users + discovery)
//
// IMPORTANT: users live in MongoDB (your Express backend's User
// model), not as Sanity documents. Every "user" field below (posts.user,
// like.user, follow.follower, follow.following) is a plain STRING
// storing the Mongo _id — never a Sanity `reference`. Sanity references
// validate the target document exists in this dataset, which it never
// will for a Mongo id, so treating these as references causes every
// write to fail with "references non-existent document" (409).
//
// Score formula (see scoreForPost / the GROQ equivalent below):
//   score = (likes*1 + comments*2 + views*0.1 + shares*1.5)
//           / (hoursSinceCreated + 2) ^ 1.5
//
// The exponent/weights are tunable constants below — adjust freely.
//
// COALESCE NOTE: every query below wraps `score` in coalesce(score, 0)
// rather than using the raw field. Posts created before the `score`
// field existed on the schema (or before the cron job ever computed
// one for them) have score MISSING, not zero — and GROQ's null
// comparisons (`null < 5`) evaluate to null, which a filter treats as
// "exclude this document". Without coalesce(), any scoreless post
// becomes permanently unreachable by cursor-based pagination past page
// 1 — invisible to every subsequent page even though it's still in the
// dataset. This is what causes infinite scroll to stop early or report
// "no more posts" while posts genuinely remain. coalesce(score, 0)
// treats a missing score as 0 for ordering/filtering purposes, closing
// that hole. The `score` field returned by the shared projection is
// coalesced too, so the client-built pagination cursor is always a
// real number, never undefined.
// ---------------------------------------------------------------------

export const PAGE_SIZE = 20;
export const FOLLOWED_RATIO = 0.7; // 70% followed, 30% discovery

const WEIGHTS = {
  like: 1,
  comment: 2,
  view: 0.1,
  share: 1.5,
};

// GRAVITY controls how fast old posts fade — same idea as Hacker
// News/Reddit "hot" ranking. Higher = steeper decay = newer content
// dominates faster. 1.5 (the old value) let a heavily-liked post stay
// competitive for days; 2.2 means a post's engagement needs to keep
// growing to hold its position — a post that stops getting engagement
// drops out of contention within hours, closer to how mainstream feeds
// behave. Tune this directly: try 1.8–2.5 and see what feels right for
// your posting volume — more posts/day generally wants a higher number
// so the feed keeps turning over.
const GRAVITY = 2.2;

// SEED_SCORE solves a different problem than GRAVITY: a brand-new post
// has had zero time to earn likes/views, so on a pure engagement
// formula it would start at score 0 — indistinguishable from a month-
// old post nobody engaged with, and only saved by the score-tie
// recency fallback (order(score desc, _createdAt desc)). Adding a flat
// seed to the numerator gives every post a baseline "free" score
// equivalent to a few likes' worth, so it's visible near the top of
// the feed the moment it's posted — then that seed decays away via the
// same (ageHours+2)^GRAVITY denominator as real engagement does, so it
// naturally fades out over the same timeframe. This is what actually
// gets a new post seen long enough to gather real engagement (the
// same cold-start problem TikTok/Instagram solve by giving new posts
// an initial small-audience test run).
const SEED_SCORE = 3;

const projection = `{
  _id,
  _createdAt,
  title,
  slug,
  image,
  status,
  name,
  avatar,
  likesCount,
  commentsCount,
  viewsCount,
  sharesCount,
  "score": coalesce(score, 0),
  tags,
  // "user" is a plain string (Mongo user _id) on every post, old and
  // new alike — no dereferencing needed. Aliased to userId so callers
  // have one stable field name regardless of how they got the post
  // (query result vs. locally-prepended new post).
  "userId": user
}`;

/**
 * Compute a post's score in JS (used by the score-updater job).
 * Mirrors the GROQ math used at query time for posts that haven't
 * been recomputed yet.
 */
export function scoreForPost(post) {
  const likes = post.likesCount || 0;
  const comments = post.commentsCount || 0;
  const views = post.viewsCount || 0;
  const shares = post.sharesCount || 0;

  const ageHours =
    (Date.now() - new Date(post._createdAt).getTime()) / 36e5;

  const rawScore =
    likes * WEIGHTS.like +
    comments * WEIGHTS.comment +
    views * WEIGHTS.view +
    shares * WEIGHTS.share +
    SEED_SCORE;

  return rawScore / Math.pow(ageHours + 2, GRAVITY);
}

/**
 * Fetch one page of the global ranked feed, ordered by score desc
 * then recency desc. Pass the last item's {score, _createdAt} as a
 * cursor to get the next page — this is real server-side pagination,
 * not a slice of an array already sitting in memory.
 */
export async function fetchFeedPage({ cursor = null, pageSize = PAGE_SIZE } = {}) {
  const query = cursor
    ? `*[_type == "posts" &&
         (coalesce(score, 0) < $cursorScore
          || (coalesce(score, 0) == $cursorScore && _createdAt < $cursorCreatedAt))]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`
    : `*[_type == "posts"]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`;

  const params = cursor
    ? {
        cursorScore: cursor.score,
        cursorCreatedAt: cursor._createdAt,
        limit: pageSize,
      }
    : { limit: pageSize };

  const results = await client.fetch(query, params);
  const nextCursor = results.length
    ? { score: results[results.length - 1].score, _createdAt: results[results.length - 1]._createdAt }
    : null;

  return {
    posts: results,
    nextCursor,
    hasMore: results.length === pageSize,
  };
}

/**
 * Fetch one page of a specific user's posts (used for the "trader
 * products" / profile view). userId is a Mongo user _id string. Same
 * cursor shape as fetchFeedPage.
 */
export async function fetchUserPostsPage(userId, { cursor = null, pageSize = PAGE_SIZE } = {}) {
  const query = cursor
    ? `*[_type == "posts" && user == $userId &&
         (coalesce(score, 0) < $cursorScore
          || (coalesce(score, 0) == $cursorScore && _createdAt < $cursorCreatedAt))]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`
    : `*[_type == "posts" && user == $userId]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`;

  const params = cursor
    ? {
        userId,
        cursorScore: cursor.score,
        cursorCreatedAt: cursor._createdAt,
        limit: pageSize,
      }
    : { userId, limit: pageSize };

  const results = await client.fetch(query, params);
  const nextCursor = results.length
    ? { score: results[results.length - 1].score, _createdAt: results[results.length - 1]._createdAt }
    : null;

  return { posts: results, nextCursor, hasMore: results.length === pageSize };
}

/**
 * Personalized blend: FOLLOWED_RATIO of the page comes from people the
 * user follows (ranked by score), the remainder is discovery content
 * from outside their graph (ranked by score, shuffled at the tie-break
 * level so discovery doesn't feel robotic). Falls back to the plain
 * global feed if the user follows no one yet. userId is a Mongo user
 * _id string.
 */
export async function fetchPersonalizedFeedPage(userId, { pageSize = PAGE_SIZE } = {}) {
  const followingIds = await client.fetch(
    `*[_type == "follow" && follower == $userId].following`,
    { userId }
  );

  if (!followingIds.length) {
    return fetchFeedPage({ pageSize });
  }

  const followedCount = Math.round(pageSize * FOLLOWED_RATIO);
  const discoveryCount = pageSize - followedCount;

  const [followedPosts, discoveryPosts] = await Promise.all([
    client.fetch(
      `*[_type == "posts" && user in $followingIds]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`,
      { followingIds, limit: followedCount }
    ),
    client.fetch(
      `*[_type == "posts" && !(user in $followingIds)]
       | order(coalesce(score, 0) desc, _createdAt desc) [0...$limit] ${projection}`,
      { followingIds, limit: discoveryCount }
    ),
  ]);

  // Interleave rather than concatenate so discovery posts aren't all
  // dumped at the bottom of the page.
  const merged = [];
  const max = Math.max(followedPosts.length, discoveryPosts.length);
  for (let i = 0; i < max; i++) {
    if (followedPosts[i]) merged.push(followedPosts[i]);
    if (discoveryPosts[i]) merged.push(discoveryPosts[i]);
  }

  return {
    posts: merged,
    // Personalized pages are cursor-less for simplicity; the caller
    // can request the next page by re-running with an incremented
    // offset stored client-side, or you can extend this with the same
    // cursor pattern as fetchFeedPage once you need deep pagination.
    hasMore: merged.length === pageSize,
  };
}

/**
 * Increment a post's view count (call once per impression, throttled).
 * setIfMissing() first because .inc() errors on posts created before
 * viewsCount existed on the schema — this makes it self-healing
 * instead of requiring every old document to be migrated first.
 */
export async function recordView(postId) {
  return client
    .patch(postId)
    .setIfMissing({ viewsCount: 0 })
    .inc({ viewsCount: 1 })
    .commit({ autoGenerateArrayKeys: true });
}

/**
 * Deterministic id for a (post, user) like — e.g. "like-<postId>-<userId>".
 * Using a fixed id instead of an auto-generated one is what makes
 * "one like per user per post" a database-level guarantee: Sanity's
 * create() mutation only succeeds if no document with that id already
 * exists, so two near-simultaneous like clicks (a fast double-click,
 * or two open tabs) can no longer both succeed and create duplicates —
 * the second one fails with a conflict, which we interpret as "this
 * click means unlike".
 */
function buildLikeId(postId, userId) {
  return `like-${postId}-${userId}`;
}

function isDocumentExistsConflict(err) {
  return err?.statusCode === 409 || /already exists/i.test(err?.message || '');
}

/** Check whether userId has already liked postId. */
export async function checkIsLiked(postId, userId) {
  if (!postId || !userId) return false;
  const likeId = buildLikeId(postId, userId);
  const existing = await client.fetch(`*[_id == $likeId][0]._id`, { likeId });
  return !!existing;
}

/**
 * Toggle a like: creates the like doc + increments count, or removes
 * it. postId is a Sanity document id (real reference target); userId
 * is a Mongo user _id string (plain string field, not a reference).
 *
 * The like document's id is deterministic (see buildLikeId) so the
 * create step is atomic — see the comment above buildLikeId for why
 * this is what actually prevents one user generating multiple likes,
 * rather than relying on a check-then-write query that has a race.
 */
export async function toggleLike(postId, userId) {
  const likeId = buildLikeId(postId, userId);

  try {
    // Bundled into one transaction so the like document and the
    // counter move together — a dropped connection between two
    // separate requests could otherwise leave one written without
    // the other.
    await client
      .transaction()
      .create({
        _id: likeId,
        _type: 'like',
        post: { _type: 'reference', _ref: postId }, // posts ARE real Sanity docs — this reference is valid
        user: userId, // Mongo user _id — plain string, not a reference
        createdAt: new Date().toISOString(),
      })
      .patch(postId, (p) => p.setIfMissing({ likesCount: 0 }).inc({ likesCount: 1 }))
      .commit();
    return { liked: true };
  } catch (err) {
    if (!isDocumentExistsConflict(err)) throw err;
  }

  // A like with this deterministic id already existed, so this click
  // means "remove it" — same behavior as before, just reached via the
  // conflict branch instead of a separate existence check.
  await client
    .transaction()
    .delete(likeId)
    .patch(postId, (p) => p.setIfMissing({ likesCount: 0 }).dec({ likesCount: 1 }))
    .commit();
  return { liked: false };
}

/**
 * Deterministic id for a (follower, following) edge — same rationale
 * as buildLikeId above: makes "you can't follow the same person
 * twice" a database-level guarantee instead of a racy check-then-write.
 */
function buildFollowId(followerId, followingId) {
  return `follow-${followerId}-${followingId}`;
}

/** Check whether followerId currently follows followingId. */
export async function checkIsFollowing(followerId, followingId) {
  if (!followerId || !followingId || followerId === followingId) return false;
  const followId = buildFollowId(followerId, followingId);
  const existing = await client.fetch(`*[_id == $followId][0]._id`, { followId });
  return !!existing;
}

/**
 * Toggle follow between two users. Both ids are Mongo user _id
 * strings — plain string fields on the follow document, not
 * references (see the file-level note on why).
 */
export async function toggleFollow(followerId, followingId) {
  const followId = buildFollowId(followerId, followingId);

  try {
    await client.create({
      _id: followId,
      _type: 'follow',
      follower: followerId,
      following: followingId,
      createdAt: new Date().toISOString(),
    });
    return { following: true };
  } catch (err) {
    if (!isDocumentExistsConflict(err)) throw err;
  }

  await client.delete(followId);
  return { following: false };
}

/**
 * Mongo user _ids of everyone who liked this post, most recent first.
 * Capped at `limit` — for a post with hundreds of likes this returns
 * only the most recent slice, which is the same tradeoff Instagram/
 * Twitter's "liked by" lists make (recency over completeness). Returns
 * raw ids only — pair with fetchUsersByIds (src/services/users.js) to
 * turn this into displayable avatar/username data.
 */
export async function getLikers(postId, { limit = 50 } = {}) {
  if (!postId) return [];
  return client.fetch(
    `*[_type == "like" && post._ref == $postId] | order(_createdAt desc) [0...$limit].user`,
    { postId, limit }
  );
}

/**
 * Mongo user _ids of everyone following this user, most recent first.
 * Same recency-capped, raw-ids-only tradeoff as getLikers above.
 */
export async function getFollowersList(userId, { limit = 50 } = {}) {
  if (!userId) return [];
  return client.fetch(
    `*[_type == "follow" && following == $userId] | order(_createdAt desc) [0...$limit].follower`,
    { userId, limit }
  );
}

/**
 * Total follower count for a user — counts every follow edge where
 * this user is the one being followed. Cheap `count()` query, no
 * document bodies fetched.
 */
export async function getFollowersCount(userId) {
  if (!userId) return 0;
  return client.fetch(`count(*[_type == "follow" && following == $userId])`, { userId });
}

/**
 * Recompute and write back the `score` field for posts updated in the
 * last `sinceHours`. Run this on a schedule (every 5-10 minutes) from
 * a standalone long-running process — see scoreUpdater.js, which uses
 * node-cron — NOT from the client, so ranking stays a cheap sort
 * (`order(score desc)`) instead of recomputing decay math per request.
 */
export async function recalculateScores(sinceHours = 72) {
  const cutoff = new Date(Date.now() - sinceHours * 36e5).toISOString();
  const posts = await client.fetch(
    `*[_type == "posts" && _createdAt > $cutoff]{_id, _createdAt, likesCount, commentsCount, viewsCount, sharesCount}`,
    { cutoff }
  );

  const tx = client.transaction();
  posts.forEach((post) => {
    tx.patch(post._id, (p) => p.set({ score: scoreForPost(post) }));
  });

  if (posts.length) await tx.commit();
  return posts.length;
}
